import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ApiService } from "app/core/api/api.service";
import { environment } from "environments/environment";
import { Observable, of } from "rxjs";
import { catchError, tap } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class BookingService {
  private _apiService = inject(ApiService);
  private _baseUrl = environment.apiBaseUrl;

  /**
   * Constructor
   */
  constructor(private _httpClient: HttpClient) {}

  /**
   * Get all players
   */
  getPlayers(filter?: any): Observable<any> {
    let params = new HttpParams();

    if (filter) {
      Object.keys(filter).forEach((key) => {
        if (
          filter[key] !== undefined &&
          filter[key] !== null &&
          filter[key] !== ""
        ) {
          params = params.append(key, filter[key]);
        }
      });
    }

    return this._httpClient.get(`${this._baseUrl}/players`, { params });
  }

  searchByContact(searchTerm: string): Observable<any> {
    if (!searchTerm || searchTerm.trim() === "") {
      return of({
        success: false,
        message: "Search term is required",
      });
    }

    const params = new HttpParams().set("search", searchTerm.trim());

    return this._httpClient
      .get(`${this._baseUrl}/players/search`, { params })
      .pipe(
        tap((response) => console.log("✅ Search response:", response)),
        catchError((error) => {
          return of({
            success: false,
            message: `Search failed: ${error.status} ${error.statusText}`,
            exists: false,
          });
        }),
      );
  }

  /**
   * Get sports data
   */
  getSports(sportKey?: string): Observable<any> {
    if (sportKey) {
      return this._httpClient.get(`${this._baseUrl}/sports/${sportKey}`);
    }
    return this._httpClient.get(`${this._baseUrl}/sports`);
  }

  searchUsers(query: string): Observable<any[]> {
    if (!query.trim()) return of([]);
    const params = new HttpParams().set("search", query);
    return this._httpClient
      .get<any[]>(`${this._baseUrl}/players`, { params })
      .pipe(catchError(() => of([])));
  }

  getVenuePlayers(
    venueId: string,
    search = "",
    page = 1,
    limit = 30,
  ): Observable<any> {
    let params = new HttpParams()
      .set("page", String(page))
      .set("limit", String(limit))
      .set("sortBy", "name")
      .set("played", "true");
    if (search.trim()) params = params.set("search", search.trim());
    return this._httpClient.get(
      `${this._baseUrl}/players/venue/${venueId}/players`,
      { params },
    );
  }

  /**
   * Get all bookings
   */
  getBookings(bookingFilter: any): Observable<any> {
    return this._apiService.post(`/booking/search`, bookingFilter);
  }

  /**
   * Get booking by ID
   */
  getBookingById(bookingId: string): Observable<any> {
    return this._apiService.get(`/booking/${bookingId}`);
  }

  getCourtAvailability(
    venueId: string,
    courtId: string,
    date: string,
  ): Observable<any> {
    const params = new HttpParams().set("date", date).set("slotMinutes", "30");
    return this._httpClient.get(
      `${this._baseUrl}/venue/${encodeURIComponent(venueId)}/courts/${encodeURIComponent(courtId)}/availability`,
      { params },
    );
  }

  /**
   * Create a new booking
   */
  createOrUpdateBooking(bookingData: any): Observable<any> {
    const id = (bookingData?.id ?? bookingData?._id ?? "").toString().trim();

    if (id) {
      // Update existing booking
      return this._apiService.patch(`booking/${id}`, bookingData);
    } else {
      // Create new booking
      return this._apiService.post(`booking`, bookingData);
    }
  }

  /**
   * Delete booking
   */
  delete(bookingId: any): Observable<any> {
    return this._apiService.delete(`bookings/${bookingId}`);
  }

  getBookingStats(): Observable<any> {
    return this._apiService.post(`booking/stats`, {});
  }

  getBookingSchedule(): Observable<any> {
    return this._apiService.post(`booking/schedule`, {});
  }

  getBookingPieChart(): Observable<any> {
    return this._apiService.post(`booking/charts/pie`, {});
  }

  getBookingOverTime(): Observable<any> {
    return this._apiService.post(`booking/charts/last-week`, {});
  }

  /**
   * Invite players to booking - players are NOT directly added to playersList
   */
  inviteToBooking(
    bookingId: string,
    invitedUserIds: string[],
    organizerPlayerId?: string,
  ): Observable<any> {
    const inviteData = {
      invitedUserIds: invitedUserIds,
      ...(organizerPlayerId ? { organizerPlayerId } : {}),
    };
    return this._apiService.post(
      `/userBooking/bookings/${bookingId}/invite`,
      inviteData,
    );
  }
  /**
   * Create a new player (admin only)
   */
  createPlayer(playerData: any): Observable<any> {
    return this._apiService.post(`/admin/players`, playerData);
  }
}
